import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Loader2, AlertCircle, Newspaper } from 'lucide-react'
import PageHeader from '@/components/feature/PageHeader'
import { useNewsItem, formatNewsDate, newsTagLabel } from '@/hooks/useNews'
import { useLanguage } from '@/localization'

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>()
  const state = useNewsItem(id)
  const { t } = useLanguage()

  // Scroll to top on mount / when navigating between posts.
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [id])

  const title =
    state.status === 'ready' ? state.item.title : state.status === 'notfound' ? t('Post not found') : t('News')

  return (
    <>
      <PageHeader title={title} />

      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link
          to="/news"
          className="mb-8 inline-flex items-center gap-1 text-sm font-bold text-grass-deep transition-colors hover:text-grass-dark"
        >
          <ArrowLeft size={14} /> {t('Back to news')}
        </Link>

        {state.status === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-deep/60">
            <Loader2 className="animate-spin" size={20} />
            {t('Loading…')}
          </div>
        )}

        {state.status === 'error' && (
          <div className="flex items-center gap-3 rounded-xl border border-stone-light/60 bg-white p-5 text-slate-deep/70 shadow-block">
            <AlertCircle className="text-gold-dark shrink-0" size={22} />
            <span>{t('This post is unavailable right now — please try again soon.')}</span>
          </div>
        )}

        {state.status === 'notfound' && (
          <div className="flex items-center gap-3 rounded-xl border border-stone-light/60 bg-white p-5 text-slate-deep/70 shadow-block">
            <Newspaper className="text-grass-dark shrink-0" size={22} />
            <span>{t("This post no longer exists or hasn't been published.")}</span>
          </div>
        )}

        {state.status === 'ready' && (
          <article className="rounded-2xl border border-stone-light/60 bg-white p-6 shadow-block sm:p-8">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              {formatNewsDate(state.item.date) && (
                <span className="text-sm font-bold text-grass-deep">
                  {formatNewsDate(state.item.date)}
                </span>
              )}
              {state.item.tag && (
                <span className="inline-flex items-center rounded-md bg-grass/15 px-2.5 py-1 font-pixel text-[9px] uppercase tracking-[0.15em] text-grass-deep">
                  {t(newsTagLabel(state.item.tag))}
                </span>
              )}
            </div>

            {state.item.excerpt && (
              <p className="mb-6 text-lg font-medium text-slate-deep/80">{state.item.excerpt}</p>
            )}

            {state.item.body && (
              <div className="whitespace-pre-line leading-relaxed text-slate-deep/80">
                {state.item.body}
              </div>
            )}

            {state.item.link && (
              <a
                href={state.item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-lg border-2 border-grass-dark/60 px-4 py-2 text-sm font-bold text-grass-deep transition-colors hover:bg-grass-dark hover:text-white"
              >
                {t('Source / read more')} <ExternalLink size={14} />
              </a>
            )}
          </article>
        )}
      </div>
    </>
  )
}
